/**
 * SeriesIntro — Guided Arc Opening Screen
 *
 * Replaces the Arrival (emotion picker) for guided series sessions.
 * Shows before any session phase begins, establishing that this is a
 * distinct, structured journey — not an open-ended session.
 *
 * Renders:
 *   - Series tag + "Session X of Y" indicator
 *   - Series title (large, italic)
 *   - Focus title + intention paragraph for this specific session
 *   - Brief intensity check-in ("How present is this for you right now?")
 *   - "Begin Session X" CTA in amber — deliberately different from regular purple
 *
 * Dark background (#0E0818) signals a different quality of container
 * from the standard session UI. This is intentional.
 */
import { useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useTranslation } from 'react-i18next'

interface SeriesIntroProps {
  seriesTitle: string
  seriesTag: string
  sessionCount: number
  sessionPosition: number
  focusTitle: string
  intention: string
  onBegin: (intensity: number) => void
  onBack: () => void
}

export default function SeriesIntro({
  seriesTitle,
  seriesTag,
  sessionCount,
  sessionPosition,
  focusTitle,
  intention,
  onBegin,
  onBack,
}: SeriesIntroProps) {
  const { t } = useTranslation()
  useScreenId('series.intro')
  // Default to 7 — most people entering a guided arc are already engaged
  const [intensity, setIntensity] = useState(7)

  return (
    <div
      className="flex-1 flex flex-col min-h-svh page-enter"
      style={{ background: '#0E0818', color: 'white' }}
    >
      {/* Top nav — back button only */}
      <div className="flex items-center px-6 pt-5 pb-3">
        <button
          onClick={onBack}
          aria-label={t('seriesIntro.goBack')}
          className="hover:opacity-70 transition-opacity"
          style={{ color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Main content — padded, scrollable on short screens */}
      <div className="flex-1 flex flex-col px-6 py-6 max-w-xl mx-auto w-full">

        {/* Series tag + session counter */}
        <div className="flex items-center gap-3 mb-8">
          <span
            className="text-xs font-medium uppercase tracking-widest px-3 py-1 rounded-full"
            style={{
              background: 'rgba(212,175,96,0.12)',
              border: '1px solid rgba(212,175,96,0.25)',
              color: '#D4AF60',
            }}
          >
            {seriesTag}
          </span>
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {t('seriesIntro.sessionOf', { n: sessionPosition, total: sessionCount })}
          </span>
        </div>

        {/* Series title */}
        <h1
          className="font-display mb-2"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1.75rem, 6vw, 2.5rem)',
            fontWeight: 400,
            fontStyle: 'italic',
            lineHeight: 1.2,
            color: 'white',
          }}
        >
          {seriesTitle}
        </h1>

        {/* Thin amber rule */}
        <div style={{ width: 40, height: 1, background: 'rgba(212,175,96,0.4)', margin: '1.5rem 0' }} />

        {/* Focus title for this session */}
        <p
          className="text-xs font-medium uppercase tracking-widest mb-4"
          style={{ color: 'rgba(212,175,96,0.7)' }}
        >
          {focusTitle}
        </p>

        {/* Intention — what this session will explore */}
        <p
          className="text-base leading-relaxed mb-10"
          style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.75 }}
        >
          {intention}
        </p>

        {/* Intensity check-in */}
        <div className="mb-10">
          <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {t('seriesIntro.howPresent')}
          </p>

          {/* 1–10 dot selector */}
          <div className="flex gap-2 mb-2">
            {Array.from({ length: 10 }, (_, i) => i + 1).map(n => {
              const isSelected = n === intensity
              const isPast = n < intensity
              return (
                <button
                  key={n}
                  onClick={() => setIntensity(n)}
                  aria-label={t('seriesIntro.intensityN', { n })}
                  className="transition-all"
                  style={{
                    flex: 1,
                    height: isSelected ? 28 : isPast ? 20 : 14,
                    borderRadius: 4,
                    background: isSelected
                      ? '#D4AF60'
                      : isPast
                        ? 'rgba(212,175,96,0.35)'
                        : 'rgba(255,255,255,0.1)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                />
              )
            })}
          </div>

          {/* Low / High labels */}
          <div className="flex justify-between">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>{t('seriesIntro.low')}</span>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>{t('seriesIntro.high')}</span>
          </div>
        </div>

        <div className="flex-1" />

        {/* Begin CTA — amber, not purple, to signal this is different */}
        <button
          onClick={() => onBegin(intensity)}
          className="w-full py-4 rounded-xl font-medium tracking-wide"
          style={{
            background: 'rgba(212,175,96,0.18)',
            border: '1px solid rgba(212,175,96,0.4)',
            color: '#D4AF60',
            cursor: 'pointer',
            fontSize: '0.9375rem',
          }}
        >
          {t('seriesIntro.beginSession', { n: sessionPosition })}
        </button>

        {/* Quiet note about what comes next */}
        <p
          className="text-xs text-center mt-4"
          style={{ color: 'rgba(255,255,255,0.2)' }}
        >
          {t('seriesIntro.followsArc', { title: seriesTitle })}{' '}
          {t('seriesIntro.takeYourTimeNoRush')}
        </p>

      </div>
    </div>
  )
}
